import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getHomeMarketOverview, getHomeMarketNews, getSymbolGroups, getSymbolGroupQuotes, validateSymbol } from '../api';
import Button from '../components/Button';
import WatchlistsManager from '../components/WatchlistsManager';
import SymbolSuggestion from '../components/SymbolSuggestion';
import TickerAutocomplete from '../components/TickerAutocomplete';
import Sparkline from '../components/Sparkline';

function relativeAge(isoDate) {
  if (!isoDate) return '';
  const ms = Date.now() - new Date(isoDate).getTime();
  const mins = Math.max(1, Math.floor(ms / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatMarketValue(row) {
  if (row?.error) return 'Unavailable';
  if (row?.display_mode === 'percent') {
    return row?.current_value == null ? '—' : `${Number(row.current_value).toFixed(2)}%`;
  }
  return row?.current_value == null ? '—' : Number(row.current_value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMarketChange(row) {
  if (row?.error || row?.change_pct == null) return '—';
  const change = Number(row.change_pct);
  return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
}

const sectionStyle = {
  border: '1px solid var(--border)',
  borderRadius: 14,
  background: 'var(--surface)',
  padding: 16,
};

const ASSET_TYPES = [
  { value: 'auto', label: 'Auto' },
  { value: 'stock', label: 'Stock' },
  { value: 'fx', label: 'Forex' },
];

function stockPath(symbol, assetType) {
  const suffix = assetType && assetType !== 'stock' ? `?asset_type=${assetType}` : '';
  return `/stock/${symbol}${suffix}`;
}

// One quote line: primary label + optional sub-label, a sparkline, and the
// value/change. Shared by the fixed index rows and the watchlist accordions.
function QuoteRow({ primary, secondary, row, onClick }) {
  const clickable = !row?.error && !!onClick;
  const up = row?.change_pct >= 0;
  return (
    <div
      onClick={() => { if (clickable) onClick(); }}
      style={{
        display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 10,
        padding: '12px 4px', borderTop: '1px solid rgba(255,255,255,0.05)',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{primary}</div>
        {secondary && <div style={{ color: 'var(--muted)', fontSize: 11 }}>{secondary}</div>}
      </div>
      <Sparkline
        points={row.points || []}
        times={row.times}
        color={row.color || (up ? '#22c55e' : '#ef4444')}
        expectedPoints={row.expected_points}
        displayMode={row.display_mode}
        width={110}
        height={34}
      />
      <div style={{ textAlign: 'right', minWidth: 90 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{formatMarketValue(row)}</div>
        <div style={{ color: up ? '#22c55e' : '#ef4444', fontSize: 12 }}>{formatMarketChange(row)}</div>
      </div>
    </div>
  );
}

// Expandable watchlist. Quotes are fetched lazily the first time it's opened.
function WatchlistAccordion({ group, navigate }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const count = (group.symbols || []).length;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null && !loading) {
      setLoading(true);
      setError('');
      getSymbolGroupQuotes(group.id)
        .then((res) => setRows(res.data?.rows || []))
        .catch(() => setError('Could not load watchlist quotes.'))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <button
        onClick={toggle}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, background: 'none', border: 'none', color: 'var(--text)',
          padding: '12px 4px', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-block', transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none', color: 'var(--muted)' }}>▸</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{group.name}</span>
        </span>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>{count} symbol{count === 1 ? '' : 's'}</span>
      </button>

      {open && (
        <div style={{ paddingBottom: 6 }}>
          {loading && <div style={{ color: 'var(--muted)', fontSize: 13, padding: '6px 4px' }}>Loading quotes…</div>}
          {!loading && error && <div style={{ color: '#ef4444', fontSize: 13, padding: '6px 4px' }}>{error}</div>}
          {!loading && !error && rows && rows.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '6px 4px' }}>No symbols in this watchlist yet.</div>
          )}
          {!loading && !error && rows && rows.map((r) => (
            <QuoteRow
              key={`${r.asset_type}:${r.symbol}`}
              primary={r.symbol}
              secondary={r.error ? 'Data unavailable' : (r.asset_type && r.asset_type !== 'stock' ? r.asset_type.toUpperCase() : '')}
              row={r}
              onClick={() => navigate(stockPath(r.symbol, r.asset_type))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MarketsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [overviewError, setOverviewError] = useState('');
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [news, setNews] = useState([]);
  const [groups, setGroups] = useState([]);

  const [query, setQuery] = useState('');
  const [assetType, setAssetType] = useState('auto');
  const [searchResult, setSearchResult] = useState(null); // resolve_symbol response when invalid
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoadingOverview(true);
    getSymbolGroups()
      .then((res) => { if (mounted) setGroups(res.data || []); })
      .catch(() => { if (mounted) setGroups([]); });

    getHomeMarketOverview()
      .then((res) => {
        if (!mounted) return;
        setOverview(res.data?.rows || []);
        setUpdatedAt(res.data?.as_of || new Date().toISOString());
        setOverviewError('');
      })
      .catch(() => {
        if (!mounted) return;
        setOverview([]);
        setOverviewError('Unable to load live market data right now.');
      })
      .finally(() => { if (mounted) setLoadingOverview(false); });

    getHomeMarketNews(8)
      .then((res) => { if (mounted) setNews(res.data?.items || []); })
      .catch(() => { if (mounted) setNews([]); });

    return () => { mounted = false; };
  }, []);

  const goToStock = (sym, at) => {
    const suffix = at && at !== 'auto' && at !== 'stock' ? `?asset_type=${at}` : '';
    navigate(`/stock/${sym}${suffix}`);
  };

  const submitSearch = async (e) => {
    e.preventDefault();
    const t = query.trim().toUpperCase();
    if (!t) return;
    // Validate first so junk ("sandp500") / wrong-class ("XAUUSD" as equity)
    // lookups get a "did you mean" instead of a dead /stock/ page.
    setSearching(true);
    setSearchResult(null);
    try {
      const { data } = await validateSymbol(t, assetType);
      if (data?.valid) { goToStock(data.symbol || t, data.asset_type || assetType); return; }
      setSearchResult(data);
    } catch {
      goToStock(t, assetType); // couldn't verify — navigate anyway rather than block
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={{ padding: '26px 26px 30px', maxWidth: 1160, margin: '0 auto' }}>
      <Helmet><title>Markets — Algovex</title></Helmet>

      <h1 style={{ margin: '0 0 6px', fontSize: 'var(--fs-xl, 28px)' }}>Markets</h1>
      <p style={{ color: 'var(--muted)', marginTop: 0, marginBottom: 18, fontSize: 14 }}>
        Live index snapshots, ticker lookups, and your watchlists — all in one place.
      </p>

      {/* Ticker search */}
      <form onSubmit={submitSearch} style={{ ...sectionStyle, display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <TickerAutocomplete
          value={query}
          onChange={(val) => { setQuery(val); if (searchResult) setSearchResult(null); }}
          onPick={(sym, at) => goToStock(sym, at)}
          placeholder="Look up a ticker (e.g. AAPL, EURUSD, BTC)"
          aria-label="Look up a ticker"
          wrapperStyle={{ flex: 1, minWidth: 180 }}
          style={{
            width: '100%', boxSizing: 'border-box', background: 'var(--bg, #0b0f14)',
            border: '1px solid var(--border)', borderRadius: 8,
            color: 'var(--text)', padding: '10px 12px', fontSize: 14,
          }}
        />
        <select
          value={assetType}
          onChange={(e) => setAssetType(e.target.value)}
          aria-label="Asset type"
          style={{
            background: 'var(--bg, #0b0f14)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text)', padding: '10px 12px', fontSize: 14,
          }}
        >
          {ASSET_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <Button type="submit" disabled={searching}>{searching ? 'Searching…' : 'Search'}</Button>
        {searchResult && !searchResult.valid && (
          <div style={{ flexBasis: '100%' }}>
            {searchResult.error && (
              <div style={{ fontSize: 12, color: 'var(--red, #f85149)' }}>{searchResult.error}</div>
            )}
            <SymbolSuggestion result={searchResult} onApply={goToStock} />
          </div>
        )}
      </form>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }} className="markets-grid">
        {/* Market overview */}
        <section style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Market Overview</div>
            {updatedAt && !overviewError && (
              <div style={{ color: 'var(--muted)', fontSize: 11 }}>Updated {relativeAge(updatedAt)}</div>
            )}
          </div>

          {loadingOverview && overview.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 0' }}>Loading live market data…</div>
          )}
          {!loadingOverview && overview.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 0' }}>
              {overviewError || 'No market data available.'}
            </div>
          )}

          {overview.map((row) => (
            <QuoteRow
              key={row.label}
              primary={row.label}
              secondary={row.symbol}
              row={row}
              onClick={row?.symbol ? () => navigate(`/stock/${row.symbol}`) : undefined}
            />
          ))}

          {groups.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
                Watchlists
              </div>
              {groups.map((g) => (
                <WatchlistAccordion key={g.id} group={g} navigate={navigate} />
              ))}
            </div>
          )}
        </section>

        {/* Latest news */}
        <section style={sectionStyle}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Latest News</div>
          {news.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>No cached news yet.</div>
          )}
          {news.slice(0, 8).map((item) => (
            <div
              key={item.id || item.url}
              onClick={() => { if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer'); }}
              style={{
                padding: '12px 0', borderTop: '1px solid rgba(255,255,255,0.05)',
                display: 'flex', justifyContent: 'space-between', gap: 10,
                cursor: item.url ? 'pointer' : 'default',
              }}
            >
              <div style={{ fontSize: 13 }}>{item.title}</div>
              <div style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{relativeAge(item.published_at)}</div>
            </div>
          ))}
        </section>
      </div>

      <section style={{ ...sectionStyle, marginTop: 12 }}>
        <WatchlistsManager subtitle="Group symbols into baskets, then run a strategy across a whole basket from the backtest results screen." />
      </section>

      <style>{`
        @media (max-width: 900px) {
          .markets-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
